import {EmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

export type PlanCodeItem = {
  phase: string;
  planCode: string;
  description: string;
  units: number;
  planLevel: string;
  tracInputL70: string;
  onTheJobRiderBenefit: number;
};

export const EmptyPlanCodeItem: PlanCodeItem = {
  phase: '',
  planCode: '',
  description: '',
  units: 0,
  planLevel: '',
  tracInputL70: '',
  onTheJobRiderBenefit: 0,
};

type PlanCodesProp = {
  planCodes: PlanCodeItem[];
  coverageLevel: StringUnion;
  packageOption: StringUnion;
  productType: StringUnion;
  replaceProduct: boolean;
  issueType: StringUnion;
  faceAmount: StringUnion;
  monthlySalary: StringUnion;
  monthlyBenefit: StringUnion;
  planOption: StringUnion;
};

export class UnvalidatedPlanCodes {
  public readonly key = 'UnvalidatedPlanCodes';

  constructor(
    public readonly planCodes: PlanCodeItem[],
    public readonly coverageLevel: StringUnion,
    public readonly packageOption: StringUnion,
    public readonly productType: StringUnion,
    public readonly replaceProduct: boolean,
    public readonly issueType: StringUnion,
    public readonly faceAmount: StringUnion,
    public readonly monthlySalary: StringUnion,
    public readonly monthlyBenefit: StringUnion,
    public readonly planOption: StringUnion
  ) {}
}

export class InvalidPlanCodes {
  public readonly key = 'InvalidPlanCodes';

  constructor(
    public readonly planCodes: PlanCodeItem[],
    public readonly coverageLevel: StringUnion,
    public readonly packageOption: StringUnion,
    public readonly productType: StringUnion,
    public readonly replaceProduct: boolean,
    public readonly issueType: StringUnion,
    public readonly faceAmount: StringUnion,
    public readonly monthlySalary: StringUnion,
    public readonly monthlyBenefit: StringUnion,
    public readonly planOption: StringUnion
  ) {}
}

export class ValidPlanCodes {
  public static createFromApp({
    planCodes,
    coverageLevel,
    packageOption,
    productType,
    replaceProduct,
    issueType,
    faceAmount,
    monthlySalary,
    monthlyBenefit,
    planOption,
  }: PlanCodesProp) {
    return this.create(
      planCodes,
      coverageLevel,
      packageOption,
      productType,
      replaceProduct,
      issueType,
      faceAmount,
      monthlySalary,
      monthlyBenefit,
      planOption
    );
  }

  public static create(
    planCodes: PlanCodeItem[],
    coverageLevel: StringUnion,
    packageOption: StringUnion,
    productType: StringUnion,
    replaceProduct: boolean,
    issueType: StringUnion,
    faceAmount: StringUnion,
    monthlySalary: StringUnion,
    monthlyBenefit: StringUnion,
    planOption: StringUnion
  ) {
    return planCodes.length > 0
      ? new ValidPlanCodes(
          planCodes,
          coverageLevel,
          packageOption,
          productType,
          replaceProduct,
          issueType,
          faceAmount,
          monthlySalary,
          monthlyBenefit,
          planOption
        )
      : new InvalidPlanCodes(
          planCodes,
          coverageLevel,
          packageOption,
          productType,
          replaceProduct,
          issueType,
          faceAmount,
          monthlySalary,
          monthlyBenefit,
          planOption
        );
  }

  public readonly key = 'ValidPlanCodes';
  private constructor(
    public readonly planCodes: PlanCodeItem[],
    public readonly coverageLevel: StringUnion,
    public readonly packageOption: StringUnion,
    public readonly productType: StringUnion,
    public readonly replaceProduct: boolean,
    public readonly issueType: StringUnion,
    public readonly faceAmount: StringUnion,
    public readonly monthlySalary: StringUnion,
    public readonly monthlyBenefit: StringUnion,
    public readonly planOption: StringUnion
  ) {}
}

export type PlanCodes = UnvalidatedPlanCodes | InvalidPlanCodes | ValidPlanCodes;

export type PlanCodesOptions<TReturn> = ToPartialMatchOptions<PlanCodes, 'key', TReturn>;

export const matchPlanCodes = <TReturn>(options: PlanCodesOptions<TReturn>) => (app: PlanCodes): TReturn => {
  switch (app.key) {
    case 'InvalidPlanCodes':
      return options.InvalidPlanCodes ? options.InvalidPlanCodes(app) : options.default(app);
    case 'UnvalidatedPlanCodes':
      return options.UnvalidatedPlanCodes ? options.UnvalidatedPlanCodes(app) : options.default(app);
    case 'ValidPlanCodes':
      return options.ValidPlanCodes ? options.ValidPlanCodes(app) : options.default(app);
  }
};

export const updatePlanCode = (index: number, units: number) => (app: PlanCodes) => {
  const newItem = app.planCodes[index];
  newItem.units = units;
  const planCodeItems = app.planCodes;
  planCodeItems[index] = newItem;
  return ValidPlanCodes.createFromApp({...app, planCodes: planCodeItems});
};

export const updateProduct = (replaceProduct: boolean) => (app: PlanCodes) =>
  ValidPlanCodes.createFromApp({...app, replaceProduct});

export const EmptyPlanCodes = new UnvalidatedPlanCodes(
  [],
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString()
);
